import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, FileText, RefreshCw, CheckCircle, ChevronRight,
  Link2, Shield, Clock, AlertTriangle, Users, Zap,
  HelpCircle, ArrowLeftRight, ExternalLink, LayoutDashboard
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { MarketingLayout } from '../../components/marketing/MarketingLayout';

const positioning = [
  {
    icon: LayoutDashboard,
    title: 'Control Tower as Control Plane',
    description: 'Control Tower orchestrates automation across systems. Your ITSM remains the system of record for tickets and service history.'
  },
  {
    icon: FileText,
    title: 'Tickets Stay in ITSM',
    description: 'Tickets remain in ServiceNow or Jira Service Management. Control Tower creates, updates, and enriches tickets without replacing them.'
  },
  {
    icon: Zap,
    title: 'Automation Layer',
    description: 'Control Tower adds automation capabilities to your existing ITSM. Workflows execute and report back to tickets.'
  }
];

const syncCapabilities = [
  {
    direction: 'Control Tower → ITSM',
    actions: [
      { action: 'Create ticket from case', description: 'New cases create corresponding tickets with full context' },
      { action: 'Update ticket status', description: 'Case progress reflected in ticket workflow state' },
      { action: 'Post evidence pack links', description: 'Compliance documentation attached to tickets' },
      { action: 'Add work notes', description: 'Automation actions logged as ticket work notes' }
    ]
  },
  {
    direction: 'ITSM → Control Tower',
    actions: [
      { action: 'Ticket creates case', description: 'Specific ticket types trigger case creation' },
      { action: 'Assignment sync', description: 'Ticket reassignment updates case ownership' },
      { action: 'Status updates', description: 'Manual ticket resolution closes related cases' },
      { action: 'Custom field sync', description: 'Business-specific fields flow between systems' }
    ]
  }
];

const itsmIntegrations = [
  {
    name: 'ServiceNow',
    status: 'Available',
    features: ['Incident management', 'Request management', 'Change management', 'CMDB sync'],
    authMethods: ['OAuth 2.0', 'Basic Auth']
  },
  {
    name: 'Jira Service Management',
    status: 'Available',
    features: ['Issue types', 'Workflows', 'Automation rules', 'Assets'],
    authMethods: ['OAuth 2.0', 'API Token']
  },
  {
    name: 'Freshservice',
    status: 'Beta',
    features: ['Ticket management', 'Service catalog', 'Asset management'],
    authMethods: ['API Key']
  },
  {
    name: 'Zendesk',
    status: 'Planned',
    features: ['Ticket management', 'Macros', 'Views'],
    authMethods: ['OAuth 2.0']
  }
];

const approvalMirroring = [
  {
    step: '1',
    title: 'Case Requires Approval',
    description: 'Policy engine determines case needs manager approval for elevated access request.'
  },
  {
    step: '2',
    title: 'Task Created in ITSM',
    description: 'Approval task created in ServiceNow/Jira assigned to the manager.'
  },
  {
    step: '3',
    title: 'Manager Approves in ITSM',
    description: 'Manager completes approval task in their familiar ITSM interface.'
  },
  {
    step: '4',
    title: 'Sync Back to Control Tower',
    description: 'Approval synced to Control Tower. Automation proceeds with provisioning.'
  }
];

const apiWebhooks = [
  {
    type: 'REST API',
    description: 'Full CRUD operations for cases, tickets, and sync configuration.',
    useCases: ['Custom integrations', 'Bulk operations', 'Reporting']
  },
  {
    type: 'Webhooks (Outbound)',
    description: 'Real-time notifications for case and ticket events.',
    useCases: ['Status updates', 'Escalation triggers', 'Custom workflows']
  },
  {
    type: 'Webhooks (Inbound)',
    description: 'Receive events from ITSM systems to trigger cases.',
    useCases: ['Ticket creation', 'Status changes', 'Assignment updates']
  }
];

const faqs = [
  {
    question: 'Do we need to migrate away from our current ITSM?',
    answer: 'No. Control Tower complements your existing ITSM rather than replacing it. Tickets continue to live in ServiceNow or Jira. Your teams use familiar interfaces. Control Tower adds automation capabilities on top.'
  },
  {
    question: 'How do you handle ITSM customizations?',
    answer: 'The integration supports custom fields, custom workflows, and custom ticket types. During setup, we map your ITSM configuration to Control Tower entities.'
  },
  {
    question: 'What happens if the sync fails?',
    answer: 'Failed syncs are retried automatically with exponential backoff. Persistent failures generate alerts. Cases can be manually linked to tickets if needed. The system maintains eventual consistency.'
  },
  {
    question: 'Can approvals happen only in ITSM without Control Tower?',
    answer: 'Yes. If managers prefer approving in their ITSM, approval tasks are created there. Approvals sync back to Control Tower automatically. Users don\'t need to learn a new system.'
  },
  {
    question: 'How is evidence linked to tickets?',
    answer: 'Evidence packs are stored in Control Tower and linked to tickets via URL. Auditors click the link to view full evidence. For ServiceNow, evidence can also be attached directly as attachments.'
  }
];

export const TicketingITSM = () => {
  return (
    <MarketingLayout>
      <div className="min-h-screen">
        {/* Hero */}
        <section className="py-16 px-6 bg-white border-b border-border">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
              <Link to="/product" className="hover:text-foreground">Product</Link>
              <ChevronRight className="w-4 h-4" />
              <span>Ticketing & ITSM</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-semibold text-foreground mb-4 tracking-tight">
              ITSM Integration
            </h1>
            <p className="text-lg text-muted-foreground mb-8 max-w-3xl leading-relaxed">
              Orchestration layer on top of your existing ITSM. Control Tower automates while
              tickets remain in ServiceNow or Jira Service Management.
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Button asChild size="default">
                <Link to="/demo/request">
                  Request Demo
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <Button asChild size="default" variant="outline">
                <Link to="/integrations">View Integrations</Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Positioning */}
        <section className="py-16 px-6 bg-secondary/30">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-semibold text-foreground mb-8">How It Works Together</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {positioning.map((item, idx) => (
                <div key={idx} className="bg-white border border-border rounded-lg p-5">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <item.icon className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="text-base font-semibold text-foreground mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Two-Way Sync */}
        <section className="py-16 px-6 bg-white border-t border-border">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <ArrowLeftRight className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-semibold text-foreground">Two-Way Sync</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-8">
              {syncCapabilities.map((sync, idx) => (
                <div key={idx} className="bg-secondary/30 border border-border rounded-lg p-6">
                  <h3 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
                    {sync.direction.includes('→ ITSM') ? (
                      <ArrowRight className="w-4 h-4 text-primary" />
                    ) : (
                      <ArrowRight className="w-4 h-4 text-primary rotate-180" />
                    )}
                    {sync.direction}
                  </h3>
                  <div className="space-y-3">
                    {sync.actions.map((action, aidx) => (
                      <div key={aidx} className="bg-white border border-border rounded-md p-3">
                        <div className="text-sm font-medium text-foreground mb-1">{action.action}</div>
                        <div className="text-xs text-muted-foreground">{action.description}</div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ITSM Integrations */}
        <section className="py-16 px-6 bg-secondary/30 border-t border-border">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-semibold text-foreground mb-8">Supported ITSM Platforms</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {itsmIntegrations.map((itsm, idx) => (
                <div key={idx} className="bg-white border border-border rounded-lg p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-base font-semibold text-foreground">{itsm.name}</h3>
                    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded ${
                      itsm.status === 'Available' ? 'bg-green-100 text-green-700' :
                      itsm.status === 'Beta' ? 'bg-amber-100 text-amber-700' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      {itsm.status}
                    </span>
                  </div>
                  <div className="mb-4">
                    <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Features</div>
                    <div className="flex flex-wrap gap-2">
                      {itsm.features.map((feature, fidx) => (
                        <span key={fidx} className="inline-flex items-center gap-1 px-2 py-0.5 bg-secondary text-xs text-muted-foreground rounded-md">
                          <CheckCircle className="w-3 h-3" />
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Auth Methods</div>
                    <div className="text-sm text-foreground">{itsm.authMethods.join(', ')}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Approval Mirroring */}
        <section className="py-16 px-6 bg-white border-t border-border">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-semibold text-foreground mb-2">Approval Mirroring</h2>
            <p className="text-muted-foreground mb-8">
              Approvers can approve in their familiar ITSM interface. Decisions sync automatically.
            </p>
            <div className="grid md:grid-cols-4 gap-4">
              {approvalMirroring.map((step, idx) => (
                <div key={idx} className="relative">
                  <div className="bg-secondary/30 border border-border rounded-lg p-4 h-full">
                    <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold mb-3">
                      {step.step}
                    </div>
                    <h3 className="text-sm font-semibold text-foreground mb-2">{step.title}</h3>
                    <p className="text-xs text-muted-foreground leading-relaxed">{step.description}</p>
                  </div>
                  {idx < approvalMirroring.length - 1 && (
                    <div className="hidden md:block absolute top-10 -right-2 w-4 text-muted-foreground">
                      <ChevronRight className="w-4 h-4" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* API & Webhooks */}
        <section className="py-16 px-6 bg-secondary/30 border-t border-border">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl font-semibold text-foreground mb-8">API & Webhooks</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {apiWebhooks.map((api, idx) => (
                <div key={idx} className="bg-white border border-border rounded-lg p-5">
                  <h3 className="text-base font-semibold text-foreground mb-2">{api.type}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-4">{api.description}</p>
                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Use Cases</div>
                  <ul className="space-y-1">
                    {api.useCases.map((useCase, uidx) => (
                      <li key={uidx} className="flex items-center gap-2 text-sm text-foreground">
                        <CheckCircle className="w-3 h-3 text-primary" />
                        {useCase}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <div className="mt-8 text-center">
              <Button asChild variant="outline" size="sm">
                <Link to="/docs">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  API Documentation
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 px-6 bg-white border-t border-border">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-semibold text-foreground mb-8">Frequently Asked Questions</h2>
            <div className="space-y-4">
              {faqs.map((faq, idx) => (
                <div key={idx} className="bg-secondary/30 border border-border rounded-lg p-5">
                  <h3 className="text-sm font-semibold text-foreground mb-2 flex items-start gap-2">
                    <HelpCircle className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                    {faq.question}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed pl-6">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 px-6 bg-primary/5 border-t border-border">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl font-semibold text-foreground mb-4">Add Automation to Your ITSM</h2>
            <p className="text-muted-foreground mb-8">
              See how organizations add automation capabilities while keeping tickets in their existing ITSM.
            </p>
            <div className="flex justify-center gap-3">
              <Button asChild size="lg">
                <Link to="/demo/request">
                  Request Demo
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Link>
              </Button>
              <Button asChild size="lg" variant="outline">
                <Link to="/integrations">View Integrations</Link>
              </Button>
            </div>
          </div>
        </section>
      </div>
    </MarketingLayout>
  );
};

export default TicketingITSM;
